/**
 * TaccuinoWaitingOverlay — iter v33 r52
 *
 * Overlay bloccante mostrato quando l'agente entra in un annuncio appena
 * creato dal taccuino (?from=taccuino). L'annuncio esiste, ma le foto
 * potrebbero non essere ancora tutte visibili nella galleria (thumbnail
 * still generating, upload async, ecc.). L'utente ci ha chiesto
 * esplicitamente: "una volta creato l'annuncio uno non possa entrare
 * finche' non vede gia' tutte le otto fotografie sotto nella galleria".
 *
 * Logica di uscita:
 *  - Se `photos.length >= expectedCount`, esce subito.
 *  - Altrimenti, aspetta finche' `photos.length` resta STABILE per 3s
 *    (nessuna nuova foto aggiunta) → esce.
 *  - Dopo 30 secondi di attesa massima, mostra un bottone "Vai
 *    all'annuncio comunque" per uscita manuale.
 */
import { useEffect, useRef, useState } from "react";
import { Spinner } from "@phosphor-icons/react";

export default function TaccuinoWaitingOverlay({ photos, expectedCount, onDismiss }) {
  const currentCount = (photos || []).filter((p) => (p?.photo_kind || "photo") === "photo").length;
  const [lastChangeTs, setLastChangeTs] = useState(Date.now());
  const [showEscape, setShowEscape] = useState(false);
  const prevCountRef = useRef(currentCount);

  // Rileva variazioni nel conteggio foto — reset timer di stabilita'.
  useEffect(() => {
    if (currentCount !== prevCountRef.current) {
      prevCountRef.current = currentCount;
      setLastChangeTs(Date.now());
    }
  }, [currentCount]);

  // Se conteggio raggiunge/supera l'atteso → esci subito.
  useEffect(() => {
    if (expectedCount && currentCount >= expectedCount) {
      onDismiss?.();
    }
  }, [currentCount, expectedCount, onDismiss]);

  // Se conteggio stabile per 3s (5s con margine) → esci comunque.
  useEffect(() => {
    const t = setInterval(() => {
      if (currentCount > 0 && Date.now() - lastChangeTs > 5000) {
        onDismiss?.();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [currentCount, lastChangeTs, onDismiss]);

  // Dopo 30s mostra escape manuale.
  useEffect(() => {
    const t = setTimeout(() => setShowEscape(true), 30000);
    return () => clearTimeout(t);
  }, []);

  const pct = expectedCount
    ? Math.min(100, Math.round((currentCount / expectedCount) * 100))
    : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#F1EFEA] backdrop-blur-sm"
      data-testid="taccuino-waiting-overlay"
    >
      <div className="max-w-sm w-full mx-6 text-center">
        <Spinner size={48} className="animate-spin text-[#7C8B6E] mx-auto mb-6" weight="bold" />
        <h2 className="font-editorial text-2xl text-[#1C1C1A] mb-2">
          Sto preparando l&apos;annuncio
        </h2>
        <p className="text-sm text-[#6B6A65] mb-6">
          Sto caricando le foto nella galleria. Un momento — ci vogliono pochi secondi.
        </p>

        {expectedCount && (
          <div className="space-y-2" data-testid="taccuino-waiting-progress">
            <div className="flex items-center justify-between text-sm text-[#1C1C1A]">
              <span>Foto caricate</span>
              <span className="font-mono tabular-nums font-bold" data-testid="taccuino-waiting-count">
                {currentCount} / {expectedCount}
              </span>
            </div>
            <div className="h-2 bg-[#EAE7DF] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {showEscape && (
          <button
            type="button"
            onClick={onDismiss}
            className="mt-8 text-sm text-[#6B6A65] hover:text-[#1C1C1A] underline"
            data-testid="taccuino-waiting-escape"
          >
            Vai all&apos;annuncio comunque
          </button>
        )}
      </div>
    </div>
  );
}
